// RecordEditSheetDataRowList - MODULE
// ***********************************************************************************************************************
import { Alert } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, SaveTaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import * as recordService from "../../../models/services/recordService";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { RecordEditSheetDataRowDialog } from "./RecordEditSheetDataRowDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

// RecordEditSheetDataRowList
export interface IRecordEditSheetDataRowListProp {
    isReadOnly: boolean;
    companyId: string;
    recordId: string;
    userCode: string;
    rowId: string;
    taskType: ITaskEditTypeItem;
    taskDataRows: ITaskDataRow[];

    onRowRemoved: (ids: string[], rowId: string) => void;
    onRowSaved: (taskDataRow: ITaskDataRow, rowId: string) => void;
}

interface IRecordEditSheetDataRowListState {
    // List
    isLoading: boolean;
    items: ITaskDataRow[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];
    selectedItemHasPrevious: boolean;
    selectedItemHasNext: boolean;

    // Dialog
    item: ITaskDataRow;
    showEditDialog: boolean;
}

export class RecordEditSheetDataRowList extends React.Component<IRecordEditSheetDataRowListProp, IRecordEditSheetDataRowListState> {
    getFilteredList = (filter: string, column: string, asc: boolean): ITaskDataRow[] => {
        const props = this.props;
        let result = props.taskDataRows ? props.taskDataRows.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            result = result.filter(i => i.matchesToFilter(lowFilter, props.taskType));
        }
        TaskDataRow.sortTaskDataRowsByColumn(result, props.taskType, column, asc);
        return result;
    };

    constructor(props: IRecordEditSheetDataRowListProp) {
        super(props);
        const taskDataRows = this.getFilteredList("", "startDate", false);
        this.state = {
            isLoading: false,
            items: taskDataRows,
            filter: "",
            sortColumn: "startDate",
            sortOrderIsAsc: false,
            selectedId: null,
            checkedIds: [],
            selectedItemHasPrevious: false,
            selectedItemHasNext: false,
            item: null,
            showEditDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const items = this.getFilteredList(filter, column, asc);
        const item = this.state.showEditDialog && this.state.item
            ? items.find(i => i.id === this.state.item.id)
            : null;
        const index = item ? items.findIndex(i => i.id === item.id) : -1;
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            items,
            item,
            showEditDialog: !!item,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < items.length - 1,
        });
    };

    componentDidUpdate(prevProps: IRecordEditSheetDataRowListProp, prevState: IRecordEditSheetDataRowListState) {
        const props = this.props;
        const state = this.state;
        if (EditItem.getHash(prevProps.taskDataRows) === EditItem.getHash(props.taskDataRows)) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getSaveData = (props: IRecordEditSheetDataRowListProp, taskDataRows: ITaskDataRow[]): SaveDataObj => {
        TaskDataRow.sortTaskDataRows(taskDataRows, props.taskType);
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("rowId", props.rowId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("saveTaskDataRows", taskDataRows.map(i => SaveTaskDataRow.createSaveTaskDataRow(props.taskType.formCols, i, null)));
        return result;
    };

    handleRemove = async () => {
        const obj = this;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const index = state.items.findIndex(i => i.id === selectedIds[0]);
        if (index < 0) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.RemoveRowConfirmation, (index + 1).toString(10)), false) === ConfirmationDialogResult.Yes) {
            const saveData = this.getSaveData(this.props, state.items.filter(i => selectedIds.indexOf(i.id) < 0));
            // Call server
            const result = await AppUtils.callService(() => recordService.recordEditSaveSheetData(saveData.obj));
            if (!result) return;
            obj.props.onRowRemoved(selectedIds, result.rowId);
        }
    };
    
    static createNewTaskDataRow = (taskType: ITaskEditTypeItem): TaskDataRow => {
        const result = new TaskDataRow();
        result.id = Base.getGuid();
        result.initializeToTaskType(taskType);
        return result;
    };

    handleAdd = () => {
        this.setState({
            item: RecordEditSheetDataRowList.createNewTaskDataRow(this.props.taskType),
            showEditDialog: true
        });
    };

    handleOpen = (id: string) => {
        if (!id) return;
        const state = this.state;
        const index = state.items.findIndex(i => i.id === id);
        if (index < 0) return;
        this.setState({
            selectedId: id,
            item: state.items[index],
            showEditDialog: true,
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    saveToDb = async (taskDataRow: ITaskDataRow): Promise<SaveSuccess> => {
        const obj = this;
        const state = this.state;
        if (!taskDataRow) return null;
        const taskDataRows = state.items.filter(i => i.id !== taskDataRow.id);
        taskDataRows.push(taskDataRow);
        const saveData = this.getSaveData(this.props, taskDataRows);
        // Call server
        const result = await AppUtils.callService(() => recordService.recordEditSaveSheetData(saveData.obj));
        if (!result) return null;
        taskDataRow.rowId = result.rowId;
        obj.props.onRowSaved(taskDataRow, result.rowId);
        return result;
    };

    handleRecordUserSheetDataRowDialogSaved = async (taskDataRow: ITaskDataRow, rowId: string, closeDialog: boolean) => {
        if (!taskDataRow) return;
        this.setState({
            showEditDialog: !closeDialog,
            selectedId: taskDataRow.id,
            item: taskDataRow
        });
        this.props.onRowSaved(taskDataRow, rowId);
    };

    handleRecordSheetDataRowDialogClose = (_refreshList: boolean) => {
        this.setState({
            showEditDialog: false
        });
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        if (index < 0 || index >= state.items.length) return;
        this.setState({
            selectedId: state.items[index].id,
            item: state.items[index],
            selectedItemHasPrevious: index > 0,
            selectedItemHasNext: index < state.items.length - 1
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        
        return (
            <>
                <VerticalContainer
                    before={<>
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddRow}
                                    removeTooltip={Translations.RemoveRow}
                                    addDisabled={props.isReadOnly}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                        {!!props.isReadOnly &&
                            <Alert severity="info">{Translations.NewRecordUserMustBeSavedBeforeRowsCanBeAdded}</Alert>
                        }
                    </>}
                    main={<>
                        {!props.isReadOnly && !!props.taskType &&
                            <List
                                intialized={true}
                                isLoading={state.isLoading}
                                hasMore={false}
                                globalLoadingInUse={false}
                                height="100%"
                                header={{
                                    columns: props.taskType.lineCols.map(i => {
                                        return { styleClass: i.class, column: i.field, content: i.title };
                                    })
                                }}
                                lines={
                                    state.items.map((dataRow) => {
                                        return {
                                            columns: props.taskType.lineCols.map((i, index) => {
                                                const uiValue = dataRow.getTaskDataRowColListUiValue(i.field, props.taskType);
                                                return {
                                                    styleClass: i.class,
                                                    column: i.field,
                                                    content: index < 0.5
                                                        ? <ListLinkValue onClick={() => this.handleOpen(dataRow.id)}>{uiValue.text}</ListLinkValue>
                                                        : uiValue.text
                                                };
                                            }),
                                            id: dataRow.id
                                        };
                                    })
                                }
                                noDataMessage={Translations.AddRows}
                                sortColumn={state.sortColumn}
                                sortOrderIsAsc={state.sortOrderIsAsc}
                                selectedId={state.selectedId}
                                checkedIds={state.checkedIds}
                                onSetSelectedIds={this.handleSetSelectedIds}
                                onSearchLines={this.handleSearchLines}
                                onOpenLine={this.handleOpen}
                            />
                        }
                    </>}
                />
                {state.showEditDialog &&
                    <RecordEditSheetDataRowDialog
                        taskType={props.taskType}
                        item={state.item}
                        hasPrevious={state.selectedItemHasPrevious}
                        hasNext={state.selectedItemHasNext}
                        saveToDb={this.saveToDb}
                        onSaved={this.handleRecordUserSheetDataRowDialogSaved}
                        onClose={this.handleRecordSheetDataRowDialogClose}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                }
            </>
        );
    }
}